import * as React from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

export const ColorModeContext = React.createContext<{
  mode: "light" | "dark";
  toggle: () => void;
}>({ mode: "light", toggle: () => {} });

function buildTheme(mode: "light" | "dark") {
  return createTheme({
    palette: {
      mode,
      primary: { main: "#2e7d32" }, // premium green
      background: {
        default: mode === "dark" ? "#070B0F" : "#F7F9FC",
        paper: mode === "dark" ? "#0B1220" : "#FFFFFF",
      },
    },
    shape: { borderRadius: 14 },
    typography: {
      fontFamily: `"Inter", system-ui, -apple-system, Segoe UI, Roboto, Arial`,
      h4: { fontWeight: 900, letterSpacing: -0.6 },
      button: { textTransform: "none", fontWeight: 800 },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 999 },
          contained: { boxShadow: "none" },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 999, fontWeight: 800 },
        },
      },
    },
  });
}

export default function Providers({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = React.useState<"light" | "dark">(() => {
    const saved = localStorage.getItem("ui_mode");
    return saved === "dark" ? "dark" : "light";
  });

  const toggle = React.useCallback(() => {
    setMode((m) => {
      const next = m === "dark" ? "light" : "dark";
      localStorage.setItem("ui_mode", next);
      return next;
    });
  }, []);

  const theme = React.useMemo(() => buildTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={{ mode, toggle }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
